import { createSlice, configureStore } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

const accountPersistConfig = {
  key: "account",
  storage
};

const quizPersistConfig = {
  key: "quiz",
  storage
};

interface AccountState {
  loggedIn: boolean;
  token: string | null;
  profile: any;
  user_id: string;
}

const accountInitialState: AccountState = {
  loggedIn: false,
  token: null,
  profile: {},
  user_id: ""
};

const accountSlice = createSlice({
  name: "account",
  initialState: accountInitialState,
  reducers: {
    login(state, action) {
      state.loggedIn = action.payload.loggedIn;
      state.token = action.payload.token;
      state.profile = { ...action.payload.profile };
      state.user_id = action.payload.user_id;
    },
    setProfile(state, action) {
      state.profile = { ...action.payload };
    },
    logout(state) {
      state.loggedIn = false;
      state.token = null;
      state.profile = {};
      state.user_id = "";
    }
  }
});

interface QuizState {
  questions: Record<string, any>;
  time_limit: number;
  start_time: string;
  score: number;
  unattemptedQuestions: any[];
  topics: any[];
  quizzes: any[];
  selectedCertificationCli: {
    quiz_id: string;
    cli_id: string;
    cli_name: string;
    reference_id: string;
    duration_months?: number;
    due_date?: string;
  } | null;
}

const quizInitialState: QuizState = {
  questions: {},
  time_limit: 0,
  start_time: "",
  score: 0,
  unattemptedQuestions: [],
  topics: [],
  quizzes: [],
  selectedCertificationCli: null,
};

const quizSlice = createSlice({
  name: "quiz",
  initialState: quizInitialState,
  reducers: {
    answerQuestion(state, action) {
      state.questions[action.payload.questionId] = {
        ...state.questions[action.payload.questionId],
        submitted: true,
        submittedAnswer: action.payload.answer,
      };
    },
    setQuestion(state, action) {
      const payload = action.payload;
      let questionsList = [];
      let quizDuration = null;

      if (payload && !Array.isArray(payload) && payload.questions && Array.isArray(payload.questions)) {
        questionsList = payload.questions;
        quizDuration = payload.quiz_duration;
      } else if (Array.isArray(payload)) {
        questionsList = payload;
        quizDuration = (payload as any)["quiz_duration"];
      }

      let redObj = questionsList.reduce((acc: any, q: any, index: number) => {
        acc[index.toString()] = {
          ...q,
          submitted: false,
          submittedAnswer: null,
        };
        return acc;
      }, {});
      state.questions = redObj;
      if (quizDuration) {
        state.time_limit = parseInt(quizDuration) * 60;
      }
    },


    setTimeRemaining: (state, { payload }) => {
      state.time_limit = payload;
    },
    setStartTime: (state, { payload }) => {
      state.start_time = payload;
    },
    setScore: (state, { payload }) => {
      state.score = payload;
    },
    setUnattemptedQuestions: (state, { payload }) => {
      state.unattemptedQuestions = payload;
    },
    setTopics: (state, { payload }) => {
      state.topics = payload;
    },
    setQuizzes: (state, { payload }) => {
      state.quizzes = payload;
    },
    setSelectedCertificationCli: (state, { payload }) => {
      state.selectedCertificationCli = payload;
    },
    clearSelectedCertificationCli: (state) => {
      state.selectedCertificationCli = null;
    },
  },
});



const persistedAccountReducer = persistReducer(
  accountPersistConfig,
  accountSlice.reducer
);
const persistedQuizReducer = persistReducer(
  quizPersistConfig,
  quizSlice.reducer
);

const store = configureStore({
  reducer: {
    account: persistedAccountReducer,
    quiz: persistedQuizReducer
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false
    })
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export { store };
export const persistor = persistStore(store);
export const { login, logout, setProfile } = accountSlice.actions;
export const {
  answerQuestion,
  setQuestion,
  setTimeRemaining,
  setStartTime,
  setScore,
  setUnattemptedQuestions,
  setTopics,
  setQuizzes,
  setSelectedCertificationCli,
  clearSelectedCertificationCli,
} = quizSlice.actions;
